// Firebase configuration
// Copy your Firebase project config from the Firebase console and paste it into the
// REACT_APP_FIREBASE_* environment variables in an .env.local file.
// Example .env.local (do NOT commit to source control):
// REACT_APP_FIREBASE_API_KEY=...
// REACT_APP_FIREBASE_AUTH_DOMAIN=...
// REACT_APP_FIREBASE_PROJECT_ID=...
// REACT_APP_FIREBASE_STORAGE_BUCKET=...
// REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...
// REACT_APP_FIREBASE_APP_ID=...
// REACT_APP_FIREBASE_MEASUREMENT_ID=...
//
// Harness / emulator mode (used by the99-test-harness, not production):
// REACT_APP_USE_EMULATORS=true
// REACT_APP_EMULATOR_HOST=localhost          # web/iOS sim
// REACT_APP_EMULATOR_HOST=10.0.2.2           # Android emulator -> host
// When USE_EMULATORS is true, Auth/Firestore/Storage connect to the local
// Firebase emulator suite. The flag is baked in at build time and is off
// for any build that does not set it, so production users are unaffected.

import { initializeApp } from "firebase/app";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
} from "firebase/auth";

const useEmulators = String(process.env.REACT_APP_USE_EMULATORS || "").toLowerCase() === "true";
const emulatorHost = String(process.env.REACT_APP_EMULATOR_HOST || "localhost");

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

if (!firebaseConfig.storageBucket) {
  console.warn(
    "REACT_APP_FIREBASE_STORAGE_BUCKET is not set. Falling back to default bucket."
  );
}

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.warn("[Auth] Failed to enable local persistence:", error);
});
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
facebookProvider.addScope("public_profile");
facebookProvider.setCustomParameters({ display: "popup" });

// Long-polling under the WebChannel transport keeps the Firestore emulator
// reachable from Capacitor WebViews and from WebKit, which both stall on the
// default streaming transport. Outside emulator builds we keep the default
// (gRPC-style) transport plus the persistent local cache.
export const db = useEmulators
  ? initializeFirestore(app, {
      experimentalForceLongPolling: true,
      useFetchStreams: false,
    })
  : initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });

export const storage = firebaseConfig.storageBucket
  ? getStorage(app, firebaseConfig.storageBucket)
  : getStorage(app);

if (useEmulators) {
  try {
    connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(db, emulatorHost, 8080);
    connectStorageEmulator(storage, emulatorHost, 9199);
    // eslint-disable-next-line no-console
    console.info(`[Firebase] Emulator mode ON — host=${emulatorHost}`);
  } catch (error) {
    console.error("[Firebase] Failed to wire emulators:", error);
  }
}
